<script lang="ts" setup>
import FormElement from '@/components/FormElement.vue'
import { camelize, ValidatorType } from '@/helper/ValidateDirective.ts'
import { CustomArticleType } from '@/helper/Types.ts'
import userStore from '@/store/UserStore.ts'
import PriceInput from '@/components/PriceInput.vue'
import SpinnerInput from '@/components/steps/accessories/SpinnerInput.vue'
import { ref, watch } from 'vue'
import formStore from '@/store/FormStore.ts'

const props = defineProps<{
  customArticle: CustomArticleType
  group: string
  validator: ValidatorType
}>()

defineEmits<{
  (e: 'deleteCustomArticle', customArticleId: string): void
}>()

const quantity = ref(findCustomArticle()?.quantity || 1)
watch(quantity, (val: number): void => changeQuantity(val))

function findCustomArticle() {
  return (formStore.data.customArticles as CustomArticleType[]).find(c => c.id === props.customArticle.id)
}

function changeQuantity(quantity: number): void {
  const customArticle = findCustomArticle()
  if (customArticle) {
    customArticle.quantity = quantity
  }
}

defineExpose({
  validator: props.validator
})
</script>

<template>
  <div class="fTable__item custom-article">
    <div class="fTable__tr has--td">
      <div class="fTable__td column is--product">
        <div class="fTable__thumbnail">
          <div class="fTable__image-box image-box has--no-bg">
            <div class="image-box__container">
              <i :class="`ic-ca-${group}`" />
            </div>
          </div>
        </div>
        <div class="fTable__product-infos">
          <FormElement
            v-validate="'required|max:120'"
            :error="validator[camelize(`custom-article-name-desktop-${customArticle.id}`)]?.error"
          >
            <input
              :id="`custom-article-name-desktop-${customArticle.id}`"
              v-model="customArticle.name"
              :placeholder="$t('step_accessories.placeholder_name')"
              class="custom-article-name"
              data-hj-suppress
              maxlength="120"
              type="text"
            >
          </FormElement>
          <span class="is--component-spec hide-desktop">
            <b>{{ $t('step_accessories.table_order_number') }}:</b>
            -
          </span>
          <span v-if="!userStore.hidePrices" class="relative is--component-spec hide-desktop">
            <PriceInput
              :id="`custom-article-price-mobile-${customArticle.id}`"
              v-model:price="customArticle.price"
              v-validate="'positiveDecimal'"
              :placeholder="$t('step_accessories.placeholder_price')"
            />
          </span>
        </div>
      </div>
      <div class="fTable__td column is--order-number hide-mobile">
        <span>-</span>
      </div>
      <div v-if="!userStore.hidePrices" class="fTable__td column is--price hide-mobile">
        <PriceInput
          :id="`custom-article-price-desktop-${customArticle.id}`"
          v-model:price="customArticle.price"
          v-validate="'positiveDecimal'"
          :error="validator[camelize(`custom-article-price-desktop-${customArticle.id}`)]?.error"
          :placeholder="$t('step_accessories.placeholder_price')"
        />
      </div>
      <div class="fTable__td column is--details hide-mobile">
        <button
          class="btn is--raw fTable__details-btn"
          type="button"
          @click="$emit('deleteCustomArticle', customArticle.id)"
        >
          {{ $t('global.delete') }}<i class="ic-trash" />
        </button>
      </div>
      <div class="fTable__td column is--spinner-controls">
        <SpinnerInput
          :id="`custom-article-${customArticle.id}`"
          v-model="quantity"
          :hide-controls-mobile="true"
          :max-val="99"
          :min-val="1"
        />
        <button
          class="btn is--raw fTable__details-btn hide-desktop"
          type="button"
          @click="$emit('deleteCustomArticle', customArticle.id)"
        >
          {{ $t('global.delete') }}
          <i class="ic-trash" />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.fTable__item {
  overflow: visible;

  &.custom-article {
    max-height: none;

    .image-box__container {
      background: transparent;

      [class^='ic-'],
      [class*=' ic-'] {
        font-size: rem-calc(56);

        @include themify($themes) {
          color: themed('primaryColor');
        }
      }
    }

    .fTable__product-infos {
      & > *:first-child {
        margin-top: rem-calc(9);
        margin-bottom: rem-calc(8);
      }

      & > *:last-child {
        margin-bottom: rem-calc(9);
      }
    }
  }

  .fTable__product-infos {
    @include media('<tablet') {
      padding: .5rem;
    }
  }
}
</style>
